import type { ReactNode } from "react";
import { datasetSummaryFrom } from "@/components/shapes/dataset-frame/dataset-values";
import { useWallFrame } from "@/components/wall/wall-frame-source";
import { useT } from "@/i18n";
import { framesChartMessages } from "@/i18n/messages/frames-chart";
import { cn } from "@/lib/cn";
import { formatRunDay } from "@/lib/schedule-format";
import { useTasksStore } from "@/stores/tasks-store";
import type { ChartKind, ChartSource } from "./chart-points";

/** The ghost bars' heights, in percent: low and calm, never a trend. */
const GHOST_BARS = [34, 48, 40, 56, 44, 52];

/** A faint outline of the chart's own shape, in the muted ink. */
function ChartGhost({ kind }: { kind: ChartKind }) {
  if (kind === "number")
    return (
      <p className="absolute left-0 top-0 font-display text-[4.6em] font-medium leading-none">
        —
      </p>
    );
  if (kind === "line")
    return (
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <line
          x1="0"
          y1="60"
          x2="100"
          y2="60"
          stroke="currentColor"
          strokeWidth={2}
          strokeDasharray="6 6"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    );
  return (
    <div className="absolute inset-0 flex items-end gap-[0.45em] pt-[0.9em]">
      {GHOST_BARS.map((height, i) => (
        <span
          key={i}
          className="min-w-0 flex-1 rounded-t-[0.3em] bg-current"
          style={{ height: `${height}%` }}
        />
      ))}
    </div>
  );
}

/**
 * A chart with nothing to draw yet: a ghost of its kind behind one short
 * line (`children`), in place of the value and drawing.
 */
export function ChartEmpty({
  kind,
  testId,
  children,
}: {
  kind: ChartKind;
  testId: string;
  children: ReactNode;
}) {
  return (
    <div
      className="relative isolate mt-auto flex min-h-[4em] flex-1 flex-col"
      data-testid={testId}
      data-ghost={kind}
    >
      <div
        className={cn(
          "absolute inset-0 -z-10 text-card-muted",
          kind === "number" ? "opacity-40" : "opacity-[0.14]",
        )}
        aria-hidden="true"
      >
        <ChartGhost kind={kind} />
      </div>
      {children}
    </div>
  );
}

/** The next run time of the project's one active schedule, if it has exactly one. */
function useOnlyNextRun(projectId: string | null): string | null {
  return useTasksStore((state) => {
    if (!projectId) return null;
    const runs = state.tasks.flatMap((task) =>
      task.project === projectId &&
      task.schedule?.is_active &&
      task.schedule.next_run_at
        ? [task.schedule]
        : [],
    );
    return runs.length === 1
      ? formatRunDay(runs[0]!.next_run_at!, runs[0]!.timezone)
      : null;
  });
}

/**
 * Why a chart is empty, in one line: its Data frame has no rows yet, or rows
 * whose bound field is still empty; then when it fills. The time shows only
 * when the project runs exactly one scheduled job, read from the loaded tasks.
 */
export function ChartEmptyLine({
  source,
  projectId,
}: {
  source: ChartSource | null;
  projectId: string | null;
}) {
  const t = useT(framesChartMessages);
  const table = useWallFrame(source?.table);
  const nextRun = useOnlyNextRun(projectId);
  const summary = table ? datasetSummaryFrom(table.content ?? {}) : null;
  const fieldId = source?.request.value_field ?? source?.request.label_field;
  const field = summary?.fields.find((item) => item.id === fieldId)?.name;
  const what =
    summary && summary.rowCount > 0 && field
      ? t("noValuesYet", { field })
      : t("noDataYet");
  const when = nextRun
    ? t("fillsAfter", { when: nextRun })
    : t("fillsAfterFirstRun");
  return `${what} · ${when}`;
}
